import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Slider, Switch, FormControlLabel } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AutoplayIcon from '@mui/icons-material/PlayCircleOutline';
import AutoplayOffIcon from '@mui/icons-material/PlayDisabled';

interface CustomVideoPlayerProps {
  src: string;
  title?: string;
  autoPlay?: boolean;
  initialMuted?: boolean;
  showControls?: boolean;
  compact?: boolean;
  onVideoEnd?: () => void;
  onFullscreen?: () => void;
  thumbnailUrl?: string;
}

const VOLUME_STORAGE_KEY = 'video-player-volume';
const MUTED_STORAGE_KEY = 'video-player-muted';
const AUTOPLAY_STORAGE_KEY = 'video-player-autoplay';

const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({
  src,
  title,
  autoPlay = true,
  initialMuted = true,
  showControls = true,
  compact = false,
  onVideoEnd,
  onFullscreen,
  thumbnailUrl
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(() => {
    const savedAutoplay = localStorage.getItem(AUTOPLAY_STORAGE_KEY);
    return savedAutoplay ? JSON.parse(savedAutoplay) : autoPlay;
  });
  const [isPlaying, setIsPlaying] = useState(isAutoplayEnabled);
  const [isMuted, setIsMuted] = useState(() => {
    const savedMuted = localStorage.getItem(MUTED_STORAGE_KEY);
    return savedMuted ? JSON.parse(savedMuted) : initialMuted;
  });
  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem(VOLUME_STORAGE_KEY);
    return savedVolume ? parseInt(savedVolume, 10) : (initialMuted ? 0 : 100);
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [shouldShowThumbnail, setShouldShowThumbnail] = useState(!isAutoplayEnabled);
  const [videoLoaded, setVideoLoaded] = useState(isAutoplayEnabled);

  // ビデオが読み込まれたときに長さを設定
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoLoaded]); // videoLoadedの変更時にも実行

  // 再生時間の更新
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (onVideoEnd) {
        onVideoEnd();
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    
    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [onVideoEnd, videoLoaded]); // videoLoadedの変更時にも実行

  // 再生/一時停止の制御
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoLoaded) return;

    if (isPlaying) {
      videoElement.play().catch(error => {
        console.error('再生できませんでした:', error);
        setIsPlaying(false);
      });
    } else {
      videoElement.pause();
    }
  }, [isPlaying, videoLoaded]);

  // 音量の制御とローカルストレージへの保存
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoLoaded) return;

    videoElement.volume = volume / 100;
    localStorage.setItem(VOLUME_STORAGE_KEY, volume.toString());
    
    if (volume === 0) {
      setIsMuted(true);
    } else if (isMuted && volume > 0) {
      setIsMuted(false);
    }
  }, [volume, isMuted, videoLoaded]);

  // ミュート状態の制御とローカルストレージへの保存
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoLoaded) return;

    videoElement.muted = isMuted;
    localStorage.setItem(MUTED_STORAGE_KEY, JSON.stringify(isMuted));
    
    if (isMuted) {
      setVolume(0);
    }
  }, [isMuted, videoLoaded]);

  // 自動再生の設定をローカルストレージに保存
  useEffect(() => {
    localStorage.setItem(AUTOPLAY_STORAGE_KEY, JSON.stringify(isAutoplayEnabled));
  }, [isAutoplayEnabled]);

  // コントロールの表示/非表示
  useEffect(() => {
    if (!showControls) return;

    const handleMouseMove = () => {
      setControlsVisible(true);
      
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) {
          setControlsVisible(false);
        }
      }, 3000);
    };

    const handleMouseLeave = () => {
      if (isPlaying) {
        setControlsVisible(false);
      }
    };

    const container = document.getElementById('video-container');
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
    }

    // 初期表示時のタイマー設定
    if (isPlaying) {
      handleMouseMove();
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [showControls, isPlaying]);

  const handleStartPlaying = () => {
    setVideoLoaded(true);
    setShouldShowThumbnail(false);
    setIsPlaying(true);
  };

  const handlePlayPause = () => {
    if (!videoLoaded) {
      handleStartPlaying();
    } else {
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (isMuted) {
      // ミュート解除時、音量が0なら30に設定
      if (volume === 0) {
        setVolume(30);
      }
    }
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = newValue as number;
    setVolume(newVolume);
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    if (!videoLoaded) return;
    const seekTime = newValue as number;
    if (videoRef.current) {
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return <VolumeOffIcon fontSize={compact ? "small" : "medium"} />;
    } else if (volume < 50) {
      return <VolumeDownIcon fontSize={compact ? "small" : "medium"} />;
    } else {
      return <VolumeUpIcon fontSize={compact ? "small" : "medium"} />;
    }
  };

  return (
    <Box 
      id="video-container"
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        backgroundColor: '#000',
        overflow: 'hidden',
      }}
    >
      {videoLoaded && (
        <video
          ref={videoRef}
          src={src}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          autoPlay={true}
          muted={isMuted}
          playsInline
          onClick={(e) => {
            e.stopPropagation();
            handlePlayPause();
          }}
        />
      )}

      {shouldShowThumbnail && thumbnailUrl && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleStartPlaying();
          }}
        >
          <img
            src={thumbnailUrl}
            alt={title || ''}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
          {/* <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              borderRadius: '50%',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box> */}
        </Box>
      )}

      {title && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '0px 8px',
            zIndex: 2,
            opacity: controlsVisible || !isPlaying ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.2, fontSize: { xs: '0.55rem', sm: '0.6rem' } }}>
            {title}
          </Typography>
        </Box>
      )}

      {showControls && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#191919',
            transition: 'opacity 0.3s',
            opacity: controlsVisible || !isPlaying ? 1 : 0,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 2,
            padding: '4px',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Box sx={{ px: 1, py: 0 }}>
            <Slider
              size="small"
              value={currentTime}
              max={duration || 100}
              onChange={handleSeek}
              disabled={!videoLoaded}
              sx={{
                color: 'primary.main',
                height: 2,
                '& .MuiSlider-thumb': {
                  width: 8,
                  height: 8,
                  display: controlsVisible ? 'block' : 'none',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0px 0px 0px 6px rgba(244, 241, 54, 0.16)',
                  },
                },
                '& .MuiSlider-rail': {
                  opacity: 0.3,
                },
                '& .MuiSlider-track': {
                  height: 2,
                },
                '@media (pointer: coarse)': {
                  padding: '12px 0',
                  margin: '-12px 0',
                },
              }}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            px: 0.5,
            height: { xs: 20, sm: 20 }
          }}>
            <IconButton 
              onClick={handlePlayPause} 
              size="small"
              sx={{ color: 'white', p: 0.5 }}
            >
              {isPlaying && videoLoaded ? <PauseIcon fontSize={compact ? "small" : "medium"} /> : <PlayArrowIcon fontSize={compact ? "small" : "medium"} />}
            </IconButton>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              ml: 1,
              mr: 2,
              flex: '0 0 auto',
              width: '100px',
              opacity: 1,
              pointerEvents: 'auto',
            }}>
              <IconButton 
                onClick={handleMuteToggle}
                size="small"
                sx={{ color: 'white', p: 0.5, mr: 1 }}
              >
                {getVolumeIcon()}
              </IconButton>
              
              <Slider
                size="small"
                value={volume}
                onChange={handleVolumeChange}
                sx={{
                  color: 'white',
                  height: 2,
                  width: '60px',
                  ml: 0.5,
                  '& .MuiSlider-thumb': {
                    width: 8,
                    height: 8,
                    '&:hover': {
                      boxShadow: '0px 0px 0px 6px rgba(255, 255, 255, 0.16)',
                    },
                  },
                  '& .MuiSlider-rail': {
                    opacity: 0.3,
                  },
                  '& .MuiSlider-track': {
                    height: 2,
                  },
                  '@media (pointer: coarse)': {
                    padding: '12px 0',
                    margin: '-12px 0',
                  },
                }}
              />
            </Box>
            
            <Typography variant="caption" sx={{ 
              color: 'white', 
              fontSize: { xs: '0.55rem', sm: '0.65rem' },
              flex: 1,
              lineHeight: 1.2,
              userSelect: 'none'
            }}>
              {videoLoaded ? `${formatTime(currentTime)} / ${formatTime(duration)}` : '--:-- / --:--'}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isAutoplayEnabled}
                  onChange={(e) => {
                    setIsAutoplayEnabled(e.target.checked);
                    if (e.target.checked && !videoLoaded) {
                      handleStartPlaying();
                    }
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      '.Mui-checked.Mui-checked + &': {
                        backgroundColor: 'primary.main',
                        opacity: 0.5,
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      width: 12,
                      height: 12,
                      marginTop: '2px',
                      marginLeft: '4px',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      transform: 'translateX(12px)',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontSize: { xs: '0.5rem', sm: '0.6rem' },
                    opacity: 0.8,
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  自動再生
                </Typography>
              }
              sx={{
                margin: 0,
                marginLeft: 1,
                marginRight: 1,
                '& .MuiFormControlLabel-label': {
                  marginLeft: 0.5,
                },
              }}
            />

            {onFullscreen && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onFullscreen();
                }}
                size="small"
                sx={{ 
                  color: 'white',
                  p: 0.5,
                }}
              >
                <FullscreenIcon fontSize={compact ? "small" : "medium"} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomVideoPlayer; 